import React, { useEffect, useState } from "react";
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { DownloadForOffline, Autorenew } from "@mui/icons-material";
import { useAvailableReports } from "services/input-core/reports/get-available-reports";
import { useDownloadReport } from "services/input-core/reports/download-report";
import { useReportsContext } from "store/reports/reportsContext";
import axios from "axios";
import { useTranslation } from "react-i18next";

const Reports: React.FC = () => {
  const { t } = useTranslation("reports");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const { isError } = useAvailableReports();
  const {
    isLoadingReport,
    disconnectSocket,
    reportsList,
    getReportsList,
    isLoadingReportList,
  } = useReportsContext();

  const { mutate: download } = useDownloadReport({
    onSuccess: async (res: any) => {
      try {
        const response = await axios.get(res?.reportUrl, {
          responseType: "blob",
        });

        const url = new URL(res.reportUrl);
        const pathParts = url.pathname.split("/");
        const filename = pathParts[pathParts.length - 1] || "download";

        const contentType = response.headers["content-type"];
        console.log("Content-Type:", contentType);

        const blob = new Blob([response.data], {
          type: contentType || "application/octet-stream",
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;

        document.body.appendChild(link);

        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        }, 100);

        handleClose();
        getReportsList();
      } catch (error) {
        setError(error.message || t("errorLoadingReports"));
      }
    },
    onError: (err: any) => {
      setError(err.message || t("reportGenerationFailed"));
    },
  });

  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, [disconnectSocket]);

  useEffect(() => {
    if (open && !isLoadingReport) {
      getReportsList();
    }
  }, [open, isLoadingReport]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setError(null);
  };

  const handleDownload = (id: string, fileName: string) => {
    if (!fileName) return;
    download(id);
  };

  const handleCloseError = () => {
    setError(null);
  };

  const id = open ? "notification-popover" : undefined;

  return (
    <div>
      <IconButton aria-describedby={id} color="inherit" onClick={handleClick}>
        <Badge badgeContent={reportsList?.length} color="error">
          <DownloadForOffline />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          ".MuiPaper-root": {
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
          },
        }}
      >
        <Box sx={{ width: 400 }}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
              px: 2,
              py: 1,
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="h6">{t("downloads")}</Typography>
          </Box>

          <List
            sx={{
              maxHeight: 400,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
              },
            }}
          >
            {isLoadingReportList ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : isError ? (
              <Typography sx={{ px: 2, py: 2 }} color="error">
                {t("errorLoadingReports")}
              </Typography>
            ) : reportsList?.length === 0 ? (
              <Typography sx={{ px: 2, py: 2 }}>
                {t("noReportsAvailable")}
              </Typography>
            ) : (
              reportsList?.map((report) => {
                const hasFile = !!report.fileName;
                const fileExtension = hasFile
                  ? report.fileName.split(".").pop()
                  : "";
                const displayName = hasFile
                  ? `${new Date(
                      report.updatedAt
                    ).toLocaleDateString()}.${fileExtension}`
                  : t("generatingReport");

                return (
                  <ListItem
                    key={report._id}
                    divider
                    button
                    onClick={() => handleDownload(report._id, report.fileName)}
                    disabled={!hasFile}
                    sx={{
                      ":hover": {
                        backgroundColor: hasFile ? "#f5f5f5" : "transparent",
                      },
                      opacity: hasFile ? 1 : 0.7,
                    }}
                  >
                    <ListItemText
                      primary={displayName}
                      secondary={new Date(
                        report.updatedAt
                      ).toLocaleDateString()}
                      primaryTypographyProps={{
                        color: hasFile ? "text.primary" : "text.disabled",
                      }}
                      secondaryTypographyProps={{
                        color: hasFile ? "text.secondary" : "text.disabled",
                      }}
                    />
                    <ListItemIcon>
                      {hasFile ? (
                        <DownloadForOffline />
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Autorenew
                            sx={{
                              animation: "spin 2s linear infinite",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                        </Box>
                      )}
                    </ListItemIcon>
                  </ListItem>
                );
              })
            )}
          </List>
        </Box>
      </Popover>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Reports;
